import axios from 'axios';
import { API_BASE, API_V1 } from '@/lib/constants';

// Ensure HTTPS in production
const ensureHttps = (url) => {
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url && url.startsWith('http:')) {
    return url.replace('http:', 'https:');
  }
  return url;
};

// Create axios instance with defaults.
// withCredentials: true ships the HttpOnly `linkhub_session` cookie set by
// the Alchemy SSO callback. Bearer-token consumers (API keys, break-glass
// admin) still work via the request interceptor below — server accepts
// either, cookie wins.
export const apiClient = axios.create({
  baseURL: ensureHttps(API_BASE),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create v1 API client
export const apiV1 = axios.create({
  baseURL: ensureHttps(API_V1),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to BOTH clients to enforce HTTPS
const httpsInterceptor = (config) => {
  if (config.baseURL) {
    config.baseURL = ensureHttps(config.baseURL);
  }
  if (config.url && config.url.startsWith('http:')) {
    config.url = ensureHttps(config.url);
  }
  return config;
};

apiClient.interceptors.request.use(httpsInterceptor);
apiV1.interceptors.request.use(httpsInterceptor);

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Helper to attempt token refresh
const attemptTokenRefresh = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return null;
  }

  try {
    // Use a fresh axios instance to avoid interceptor loops
    const response = await axios.post(`${API_V1}/auth/refresh`, {
      refresh_token: refreshToken
    });

    if (response.data?.success) {
      const { access_token, refresh_token: newRefreshToken } = response.data.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('refreshToken', newRefreshToken);
      return access_token;
    }
  } catch (error) {
    console.warn('Token refresh failed:', error);
  }
  return null;
};

// Helper to handle session expiry and redirect.
// SSO-first: send users to the server-rendered access-denied page which
// presents the "Sign in via Alchemy" CTA. The page links to the local
// /login form for break-glass admin entry. We pass the desired return
// path as a `redirectUri` query param so the Alchemy callback can land
// the user back where they were.
const handleSessionExpiry = () => {
  const currentPath = window.location.pathname + window.location.search;
  // Sign-in surfaces, by path only: /login?partner=1 and the partner
  // set-password page (/accept-invite?token=...) must not bounce to SSO.
  const isOnLoginRoute = ['/login', '/', '/accept-invite'].includes(window.location.pathname);
  if (!isOnLoginRoute && currentPath) {
    localStorage.setItem('redirectAfterLogin', currentPath);
  }

  // Clear any stale legacy auth data so we never present a half-logged-in UI.
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');

  if (isOnLoginRoute) {
    // Already on a sign-in surface — don't loop.
    return;
  }

  const redirectUri = encodeURIComponent(currentPath || '/dashboard');
  window.location.href = `/api/auth/external/access-denied?redirectUri=${redirectUri}`;
};

// Request interceptor for auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiV1.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling with token refresh
// `destination_url` -> "Destination URL", so a validation error names the
// field the user actually has to fix.
const humanizeField = (field) =>
  String(field)
    .replace(/_/g, ' ')
    .replace(/\b(url|utm|qr|id|api)\b/gi, (m) => m.toUpperCase())
    .replace(/^./, (c) => c.toUpperCase());

/**
 * Flatten FastAPI's several `detail` shapes into one readable sentence.
 *
 *   422 validation -> [{loc: ["body","destination_url"], msg: "Value error, ..."}]
 *   HTTPException  -> {code: "NOT_PENDING", message: "User is not pending"}
 *   plain          -> "Some message"
 *
 * Only the last is a string, so handing the others to a toast renders
 * "[object Object]" and the user never learns what went wrong.
 * Returns null when there's nothing usable so callers can fall through.
 */
export const extractErrorMessage = (detail) => {
  if (!detail) return null;
  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (typeof d === 'string') return d;
        // Pydantic prefixes custom validator errors with "Value error, ".
        const msg = String(d?.msg || d?.message || '').replace(/^value error,\s*/i, '');
        if (!msg) return null;
        // loc looks like ["body", "destination_url"]; the tail is the field.
        const field = Array.isArray(d?.loc)
          ? d.loc.filter((p) => typeof p === 'string' && p !== 'body').pop()
          : null;
        return field ? `${humanizeField(field)}: ${msg}` : msg;
      })
      .filter(Boolean);
    return parts.length ? parts.join(' • ') : null;
  }

  if (typeof detail === 'object') return detail.message || detail.msg || null;
  return null;
};

const createResponseInterceptor = (instance) => {
  return async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      const { status, data } = error.response;

      // Handle 401 Unauthorized - attempt token refresh
      if (status === 401 && !originalRequest._retry) {
        // Don't try to refresh if this is already a refresh/login request
        if (originalRequest.url?.includes('/auth/refresh') || 
            originalRequest.url?.includes('/auth/login')) {
          handleSessionExpiry();
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // Wait for the refresh to complete
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await attemptTokenRefresh();
          
          if (newToken) {
            // Token refresh successful - retry the original request
            processQueue(null, newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          } else {
            // Refresh failed - redirect to login
            processQueue(error, null);
            handleSessionExpiry();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          handleSessionExpiry();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Extract error message from various possible response formats.
      //
      // FastAPI shapes `detail` three different ways and only one is a string:
      //   422 validation -> [{loc: ["body","destination_url"], msg: "..."}, ...]
      //   HTTPException  -> {code: "...", message: "..."}  (our own raises)
      //   plain          -> "some message"
      // Passing the first two straight to a toast renders "[object Object]",
      // which is what users were seeing instead of the actual problem.
      const message =
        extractErrorMessage(data?.detail) ||
        data?.error?.message ||
        data?.message ||
        'An error occurred';

      return Promise.reject({
        status,
        message,
        detail: data?.detail,
        code: data?.detail?.code || data?.error?.code,
        details: data?.error?.details,
        response: error.response,
      });
    }

    if (error.request) {
      // Request made but no response
      return Promise.reject({
        status: 0,
        message: 'Network error - please check your connection',
        code: 'NETWORK_ERROR',
      });
    }

    return Promise.reject(error);
  };
};

apiClient.interceptors.response.use((response) => response, createResponseInterceptor(apiClient));
apiV1.interceptors.response.use((response) => response, createResponseInterceptor(apiV1));

export default apiClient;
